import type {DestinationRequestNode, LocationDetails} from '@logistics-frontend/types/logistic-dispatcher/external-api'
import {applySpec, flow, get, join, merge, pipe, reverse, split, toUndefinedIfEmpty} from '@logistics-frontend/utils'

import type {DeliveryInterval, DestinationPoint, NddClaimDestinationPoint} from 'src/shared/types'
import {DestinationPointDeliveryMethod} from 'src/shared/types'

const mapDoorPropsToRequestNodeDetails = applySpec<LocationDetails>({
  description: flow(get('city'), toUndefinedIfEmpty),
  full_address: flow(get('fullName'), toUndefinedIfEmpty),
  full_text: flow(get('fullName'), toUndefinedIfEmpty),
  short_text: flow(get('shortName'), toUndefinedIfEmpty),
  door_code: flow(get('doorCode'), toUndefinedIfEmpty),
  porch: flow(get('porch'), toUndefinedIfEmpty),
  sflat: flow(get('flat'), toUndefinedIfEmpty),
  sfloor: flow(get('floor'), toUndefinedIfEmpty),
  country: flow(get('country'), toUndefinedIfEmpty),
  street: flow(get('street'), toUndefinedIfEmpty),
  house: flow(get('building'), toUndefinedIfEmpty),
  locality: flow(get('city'), toUndefinedIfEmpty),
})

function fromDestinationDoorPoint(
  destinationPoint?: DestinationPoint,
  deliveryInterval?: DeliveryInterval,
): DestinationRequestNode {
  const details = mapDoorPropsToRequestNodeDetails(get('address', destinationPoint))
  const longitude = get('address.coordinates[0]', destinationPoint)
  const latitude = get('address.coordinates[1]', destinationPoint)

  return {
    type: 'custom_location',
    interval_utc: deliveryInterval,
    custom_location: {
      longitude,
      latitude,
      details: merge(details, {
        point: [longitude, latitude],
      }),
    },
  }
}

function fromDestinationPickupPoint(
  destinationPoint?: DestinationPoint,
  deliveryDate?: string,
): NddClaimDestinationPoint {
  return {
    type: 'platform_station',
    platform_station: {
      platform_id: get('pickUpPoint.point.id', destinationPoint),
    },
    interval_utc: {
      from: pipe(deliveryDate, split('.'), reverse, join('-')),
      to: pipe(deliveryDate, split('.'), reverse, join('-')),
    },
  }
}

export function fromNddDestinationPoint(
  destinationPoint?: DestinationPoint,
  deliveryInterval?: DeliveryInterval,
  deliveryDate?: string,
): NddClaimDestinationPoint {
  const deliveryMethod = get('_deliveryMethod', destinationPoint)

  switch (deliveryMethod) {
    case DestinationPointDeliveryMethod.PickUpPoint:
      return fromDestinationPickupPoint(destinationPoint, deliveryDate)
    case DestinationPointDeliveryMethod.Address:
      return fromDestinationDoorPoint(destinationPoint, deliveryInterval)
    default:
      throw new Error(`Unknown delivery method type: ${deliveryMethod}`)
  }
}
